import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { CandidateView } from "@portal/schemas";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  NativeSelect,
  NativeSelectOption,
} from "@portal/ui";
import { useMemo } from "react";
import { decideCandidate } from "../../api/client";
import { useCandidates, useInvalidate, useMe } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { DataTable, createDataTableColumns } from "../../components/DataTable";
import { InlineError } from "../../components/InlineError";
import { ApproveCandidateDialog } from "./ApproveCandidateDialog";
import {
  CANDIDATE_STATUS_LABELS,
  CandidateStatusBadge,
  OUTCOME_LABELS,
} from "./CandidateStatusBadge";
import { SyncCard } from "./SyncCard";
import { CardTitleWithHint } from "../../components/Hint";
import { ChevronLeft, EyeOff } from "lucide-react";

const SOURCE_LABELS: Record<CandidateView["source"], string> = {
  BACKLOG_LEAD: "Backlog リード",
  SHEET: "スプレッドシート",
  MANUAL: "手動",
};

const col = createDataTableColumns<CandidateView>();
const FILTERS = [
  { columnId: "outcome", label: "結果" },
  { columnId: "source", label: "由来" },
  { columnId: "assignee", label: "担当者" },
] as const;

/** プロジェクト候補の一覧と承認(SPEC §3.1 / §5.1)。既定は未処理だけを表示する。 */
export function CandidatesPage() {
  const search = useSearch({ from: "/app/projects/candidates" });
  const navigate = useNavigate();
  const status = search.status ?? "PENDING";
  const candidates = useCandidates({ status });
  const me = useMe();
  const isAdmin = me.data?.user.roles.includes("SYSTEM_ADMIN") ?? false;
  const invalidate = useInvalidate();
  // 非表示(API では却下。プロジェクトにしない候補を一覧から外す)
  const decide = useMutation({
    mutationFn: (input: { id: string; revision: number }) =>
      decideCandidate(input.id, "reject", { revision: input.revision }),
    onSuccess: () => invalidate.candidates(),
  });

  const columns = useMemo(
    () =>
      col.columns([
        col.accessor("title", {
          header: "件名",
          cell: (info) => {
            const c = info.row.original;
            return (
              <>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:underline"
                >
                  {c.title}
                </a>
                {c.resultProjectId && c.resultProjectName && (
                  <div className="text-xs text-muted-foreground">
                    →{" "}
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: c.resultProjectId }}
                      className="hover:underline"
                    >
                      {c.resultProjectName}
                    </Link>
                  </div>
                )}
                {c.decisionNote && (
                  <div className="text-xs text-muted-foreground">{c.decisionNote}</div>
                )}
              </>
            );
          },
        }),
        col.accessor("customerNameHint", { header: "顧客名(推定)" }),
        col.accessor((c) => SOURCE_LABELS[c.source], {
          id: "source",
          header: "由来",
          cell: (info) => {
            const c = info.row.original;
            return (
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline">{SOURCE_LABELS[c.source]}</Badge>
                <Badge variant="info">{OUTCOME_LABELS[c.outcome]}</Badge>
                {c.attributes["受注確度"] && (
                  <Badge variant="outline">{c.attributes["受注確度"]}</Badge>
                )}
              </div>
            );
          },
        }),
        // 絞り込み専用(表示は「由来」の列にまとめる)
        col.accessor((c) => OUTCOME_LABELS[c.outcome], {
          id: "outcome",
          header: "結果",
          meta: { className: "hidden" },
          enableSorting: false,
        }),
        col.accessor((c) => c.assigneeName ?? "", { id: "assignee", header: "担当者" }),
        col.accessor((c) => c.sourceUpdatedAt.slice(0, 10), {
          id: "updatedAt",
          header: "更新",
          meta: { className: "whitespace-nowrap" },
        }),
        col.accessor((c) => CANDIDATE_STATUS_LABELS[c.status], {
          id: "status",
          header: "状態",
          cell: (info) => {
            const c = info.row.original;
            return (
              <>
                <CandidateStatusBadge status={c.status} />
                {c.decidedByName && (
                  <div className="text-xs text-muted-foreground">{c.decidedByName}</div>
                )}
              </>
            );
          },
        }),
        col.display({
          id: "actions",
          header: "",
          cell: (info) => {
            const c = info.row.original;
            if (!c.canDecide) return null;
            return (
              <div className="flex justify-end gap-1">
                {c.status !== "APPROVED" && c.status !== "REJECTED" && (
                  <ApproveCandidateDialog key={c.revision} candidate={c} />
                )}
                {c.status !== "APPROVED" && c.status !== "REJECTED" && (
                  // 操作は「承認」と「非表示」だけ(ユーザー要望 2026-10-15。保留・再提示はやめた)。
                  // 「承認」と同じく文字つきのボタンにする(表の行の操作をアイコンだけにする決まりの例外)
                  <Button
                    variant="outline"
                    size="sm"
                    title="プロジェクトにしない候補を一覧から外す"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: c.id, revision: c.revision })}
                  >
                    <EyeOff />
                    非表示
                  </Button>
                )}
              </div>
            );
          },
        }),
      ]),
    [decide],
  );

  return (
    <div className="flex flex-col gap-4">
      {isAdmin && <SyncCard />}
      <Card data-testid="candidates-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            {/* ナビに「候補」タブは無いので、一覧へ戻る道を置く(2026-10-15) */}
            <Link
              to="/projects"
              className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft aria-hidden="true" className="size-3.5" />
              プロジェクト一覧
            </Link>
            <CardTitleWithHint hint="Backlog のリード管理で受注・完了になった課題です。承認すると正式なプロジェクトになります。">
              プロジェクト候補
            </CardTitleWithHint>
          </div>
          <NativeSelect
            aria-label="状態で絞り込み"
            value={status}
            onChange={(e) =>
              navigate({
                to: "/projects/candidates",
                search: { status: e.target.value as CandidateView["status"] },
              })
            }
          >
            {Object.entries(CANDIDATE_STATUS_LABELS).map(([k, v]) => (
              <NativeSelectOption key={k} value={k}>
                {v}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </CardHeader>
        <CardContent>
          <CardStatus
            isPending={candidates.isPending}
            isError={candidates.isError}
            refetch={() => candidates.refetch()}
          />
          <InlineError error={decide.error} />
          {candidates.data && (
            <DataTable
              columns={columns}
              data={candidates.data.candidates}
              getRowId={(c) => c.id}
              emptyText={`${CANDIDATE_STATUS_LABELS[status]}の候補はありません。`}
              searchPlaceholder="件名・顧客名・担当者で検索"
              filters={FILTERS}
              initialSorting={[{ id: "updatedAt", desc: true }]}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
